/**
 * ShopScreen — Nutrimax E-Commerce Demo Flow
 * Steps: Browse → Cart → Checkout → QRIS (10s) → Invoice (10s) → Delivering (10s) → Tracking Map (10s)
 */
import React, { useState, useEffect, useCallback } from 'react';
import * as api from './api';
import { useAuth } from './AuthContext';

const CAT_FILTERS = [
  ['', '🛍️ Semua'],
  ['pregnancy', '🤰 Kehamilan'],
  ['immunity', '🛡️ Imunitas'],
  ['children', '🧒 Anak'],
  ['detox', '🌿 Detox'],
  ['bone', '🦴 Tulang'],
  ['women', '💃 Wanita'],
  ['general', '✨ Umum'],
];

function QRISCode({ data, amount }) {
  const cells = 21;
  const grid = Array.from({ length: cells }, (_, r) =>
    Array.from({ length: cells }, (_, c) => {
      if ((r < 7 && c < 7) || (r < 7 && c >= cells - 7) || (r >= cells - 7 && c < 7)) {
        const rr = r >= cells - 7 ? r - (cells - 7) : r;
        const cc = c >= cells - 7 ? c - (cells - 7) : c;
        return rr === 0 || rr === 6 || cc === 0 || cc === 6 || (rr >= 2 && rr <= 4 && cc >= 2 && cc <= 4);
      }
      const code = (data || 'NUTRIMAX').charCodeAt((r * cells + c) % (data || 'NUTRIMAX').length);
      return ((code + r * 3 + c * 7) % 3) !== 0;
    })
  );
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#7c3aed', fontWeight: 700, marginBottom: 8, letterSpacing: 1 }}>QRIS — NutriSakti Payment</div>
      <div style={{ display: 'inline-block', background: '#fff', padding: 16, borderRadius: 12, border: '3px solid #7c3aed' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cells}, 6px)`, gap: 0.5 }}>
          {grid.flat().map((on, i) => (
            <div key={i} style={{ width: 6, height: 6, background: on ? '#1e1e2e' : '#fff', borderRadius: 0.5 }} />
          ))}
        </div>
      </div>
      <div style={{ marginTop: 10, fontSize: 18, color: '#7c3aed', fontWeight: 800 }}>
        Rp {amount?.toLocaleString('id-ID')}
      </div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Merchant: PT Suryaprana Nutrisindo</div>
    </div>
  );
}

function ProgressBar({ stage, stages }) {
  const idx = stages.indexOf(stage);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, margin: '16px 0' }}>
      {stages.map((s, i) => (
        <React.Fragment key={s}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: i <= idx ? '#7c3aed' : '#334155',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, color: '#fff', fontWeight: 700,
            transition: 'all 0.5s',
          }}>{i < idx ? '✓' : i + 1}</div>
          {i < stages.length - 1 && (
            <div style={{ flex: 1, height: 3, background: i < idx ? '#7c3aed' : '#334155', borderRadius: 2, transition: 'all 0.5s' }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function LoadingSpinner({ message }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 20px' }}>
      <div style={{ fontSize: 48, marginBottom: 16, animation: 'spin 1.5s linear infinite' }}>⏳</div>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0' }}>{message}</div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Mohon tunggu sebentar...</div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function ShopScreen({ theme, S }) {
  const { user } = useAuth();
  const motherId = user?.mother_id || 'MTR001';

  const [step, setStep] = useState('browse');
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState('');
  const [cart, setCart] = useState({ items: [], total_idr: 0, total_usdc: 0, count: 0 });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [summary, setSummary] = useState(null);
  const [address, setAddress] = useState('Jl. Timor Raya No.12, Kupang, NTT 85119');
  const [payMethod, setPayMethod] = useState('qris');
  const [payOptions, setPayOptions] = useState([]);
  const [order, setOrder] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [msg, setMsg] = useState('');
  const [orders, setOrders] = useState([]);
  const [viewTab, setViewTab] = useState('products');

  const t = theme;

  const loadProducts = useCallback(() => {
    api.getProducts(category ? { category } : {}).then(setProducts);
  }, [category]);

  const loadCart = useCallback(() => {
    api.getCart().then(setCart);
  }, []);

  const loadOrders = useCallback(() => {
    api.getOrders({ mother_id: motherId }).then(setOrders);
  }, [motherId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { if (step === 'cart') loadCart(); }, [step, loadCart]);
  useEffect(() => { if (viewTab === 'orders') loadOrders(); }, [viewTab, loadOrders]);

  const addToCart = async (productId) => {
    setMsg('');
    const res = await api.addToCart(productId, 1);
    if (res.success) {
      setMsg('✅ Ditambahkan ke keranjang!');
      loadCart();
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const updateQty = async (productId, qty) => {
    await api.updateCart(productId, qty);
    loadCart();
  };

  const doCheckout = async () => {
    if (!address.trim()) { setMsg('❌ Masukkan alamat pengiriman'); return; }
    setLoading(true); setLoadingMsg('Memvalidasi keranjang...');
    const res = await api.checkout(motherId, address, payMethod);
    if (res.success) {
      setSummary(res.summary);
      setPayOptions(res.payment_options);
      setStep('payment');
    } else {
      setMsg('❌ ' + (res.error || 'Checkout gagal'));
    }
    setLoading(false);
  };

  // Demo flow: Pay → Invoice → Deliver → Track
  const doPay = async () => {
    if (!summary) return;
    setStep('processing');
    setLoadingMsg('💳 Memproses pembayaran QRIS...');
    setLoading(true);
    const items = summary.items.map(i => ({ product_id: i.product_id, qty: i.qty }));
    const res = await api.pay({ mother_id: motherId, address: summary.address, payment_method: payMethod, items });
    setLoading(false);
    if (res.success) {
      setOrder(res);
      setStep('paid');
    } else {
      setMsg('❌ ' + (res.error || 'Pembayaran gagal'));
      setStep('payment');
    }
  };

  const doInvoice = async () => {
    if (!order) return;
    setLoadingMsg('📄 Generating invoice...');
    setLoading(true);
    const res = await api.generateInvoice(order.order_id);
    setLoading(false);
    if (res.success) {
      setInvoice(res.invoice);
      setStep('invoice');
    }
  };

  const doDeliver = async () => {
    if (!order) return;
    setLoadingMsg('📦 Mengirim ke kurir...');
    setLoading(true);
    const res = await api.startDelivery(order.order_id);
    setLoading(false);
    if (res.success) {
      setDelivery(res.delivery);
      setStep('delivering');
    }
  };

  const doTracking = async () => {
    if (!order) return;
    setLoadingMsg('🗺️ Memuat peta tracking...');
    setLoading(true);
    const res = await api.getTracking(order.order_id);
    setLoading(false);
    if (res.success) {
      setTracking(res.tracking);
      setStep('tracking');
    }
  };

  const reset = () => {
    setStep('browse'); setSummary(null); setOrder(null); setInvoice(null);
    setDelivery(null); setTracking(null); setMsg(''); setSelectedProduct(null);
    loadProducts(); loadCart();
  };

  // ── Loading state ───────────────────────────────────────────────────────────
  if (loading) return <LoadingSpinner message={loadingMsg} />;

  // ── Product Detail ──────────────────────────────────────────────────────────
  if (selectedProduct) return (
    <div>
      <button onClick={() => setSelectedProduct(null)} style={{ background: t.border, border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', color: t.text, fontSize: 13, marginBottom: 16 }}>← Kembali</button>
      <div style={{ background: t.surface, borderRadius: 16, padding: 24, border: `1px solid ${t.border}` }}>
        <div style={{ textAlign: 'center', fontSize: 64, marginBottom: 12 }}>{selectedProduct.image_emoji}</div>
        <div style={{ textAlign: 'center', fontSize: 11, color: t.textFaint, marginBottom: 4 }}>{selectedProduct.brand} · {selectedProduct.unit}</div>
        <div style={{ textAlign: 'center', fontSize: 20, fontWeight: 800, color: t.text, marginBottom: 8 }}>{selectedProduct.name}</div>
        <div style={{ textAlign: 'center', fontSize: 13, color: t.textMuted, marginBottom: 16, lineHeight: 1.6 }}>{selectedProduct.description}</div>

        <div style={{ background: t.bg, borderRadius: 12, padding: 14, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.accent, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>✨ Manfaat & Detail</div>
          <div style={{ fontSize: 13, color: t.text, lineHeight: 1.8 }}>{selectedProduct.details}</div>
        </div>

        <div style={{ background: t.bg, borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', marginBottom: 6 }}>💊 Anjuran Pemakaian</div>
          <div style={{ fontSize: 13, color: t.text }}>{selectedProduct.dosage}</div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: t.accent }}>Rp {selectedProduct.price_idr?.toLocaleString('id-ID')}</div>
            <div style={{ fontSize: 12, color: t.textFaint }}>{selectedProduct.price_usdc} USDC · {selectedProduct.unit}</div>
          </div>
          <div style={{ background: '#15803d22', color: '#15803d', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>✅ Tersedia</div>
        </div>

        <button onClick={() => { addToCart(selectedProduct.id); setSelectedProduct(null); }} style={{ width: '100%', background: t.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
          🛒 Tambah ke Keranjang
        </button>
      </div>
    </div>
  );

  // ── Step: Browse ────────────────────────────────────────────────────────────
  if (step === 'browse') return (
    <div>
      <div style={{ background: 'linear-gradient(135deg,#4c1d95,#7c3aed)', borderRadius: 14, padding: S.isMobile ? '16px 18px' : '20px 24px', marginBottom: 14, color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: S.isMobile ? 18 : 22, fontWeight: 800 }}>🏪 Nutrimax Store</div>
            <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>Nature's Way for a Better Living</div>
          </div>
          <button onClick={() => { setStep('cart'); loadCart(); }} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            🛒 {cart.count > 0 && <span style={{ background: '#e91e63', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginLeft: 4 }}>{cart.count}</span>}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[['products','🛍️ Produk'],['orders','📦 Pesanan']].map(([id, label]) => (
          <button key={id} onClick={() => setViewTab(id)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, background: viewTab === id ? t.accent : t.border, color: viewTab === id ? '#fff' : t.text }}>{label}</button>
        ))}
      </div>

      {msg && <div style={{ background: '#15803d22', color: '#15803d', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 600 }}>{msg}</div>}

      {viewTab === 'products' && (
        <>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {CAT_FILTERS.map(([val, label]) => (
              <button key={val} onClick={() => setCategory(val)} style={{ padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${category === val ? t.accent : t.border}`, background: category === val ? t.accentBg : 'transparent', color: category === val ? t.accent : t.textMuted, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{label}</button>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: S.isMobile ? '1fr' : 'repeat(2,1fr)', gap: 10 }}>
            {products.map(p => (
              <div key={p.id} onClick={() => setSelectedProduct(p)} style={{ background: t.surface, borderRadius: 14, padding: 14, border: `1px solid ${t.border}`, cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start', transition: 'all 0.2s' }}>
                <div style={{ fontSize: 36, flexShrink: 0 }}>{p.image_emoji}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 2 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: t.textFaint, marginBottom: 4 }}>{p.brand} · {p.unit}</div>
                  <div style={{ fontSize: 11, color: t.textMuted, lineHeight: 1.4, marginBottom: 6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{p.description}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontWeight: 800, color: t.accent, fontSize: 15 }}>Rp {p.price_idr?.toLocaleString('id-ID')}</div>
                    <button onClick={(e) => { e.stopPropagation(); addToCart(p.id); }} style={{ background: t.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>+ Beli</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {viewTab === 'orders' && (
        <div>
          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: t.textFaint }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📦</div>
              <div>Belum ada pesanan</div>
            </div>
          ) : orders.map(o => (
            <div key={o.id} style={{ background: t.surface, borderRadius: 12, padding: 14, border: `1px solid ${t.border}`, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: t.text }}>{o.id}</div>
                <span style={{ background: '#7c3aed', color: '#fff', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{o.status}</span>
              </div>
              <div style={{ fontSize: 12, color: t.textFaint }}>{new Date(o.created_at).toLocaleDateString('id-ID')}</div>
              <div style={{ fontWeight: 700, color: t.accent, fontSize: 14, marginTop: 4 }}>Rp {(o.total_idr || Math.round(o.total_usdc * 15400))?.toLocaleString('id-ID')}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── Step: Cart ──────────────────────────────────────────────────────────────
  if (step === 'cart') return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <button onClick={() => setStep('browse')} style={{ background: t.border, border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: t.text, fontSize: 13 }}>← Kembali</button>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>🛒 Keranjang</div>
      </div>

      {cart.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: t.textFaint }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
          <div>Keranjang kosong</div>
          <button onClick={() => setStep('browse')} style={{ marginTop: 16, background: t.accent, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>Mulai Belanja</button>
        </div>
      ) : (
        <>
          {cart.items.map(item => (
            <div key={item.product_id} style={{ background: t.surface, borderRadius: 12, padding: 14, border: `1px solid ${t.border}`, marginBottom: 10, display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{item.product?.image_emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: t.text }}>{item.product?.name}</div>
                <div style={{ fontSize: 11, color: t.textFaint }}>{item.product?.unit} · Rp {item.product?.price_idr?.toLocaleString('id-ID')}</div>
                <div style={{ fontWeight: 700, color: t.accent, fontSize: 14, marginTop: 2 }}>Rp {item.subtotal_idr?.toLocaleString('id-ID')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => updateQty(item.product_id, item.qty - 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: `1px solid ${t.border}`, background: t.bg, color: t.text, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>−</button>
                <span style={{ fontSize: 14, fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                <button onClick={() => updateQty(item.product_id, item.qty + 1)} style={{ width: 26, height: 26, borderRadius: '50%', border: `1px solid ${t.border}`, background: t.bg, color: t.text, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
              </div>
            </div>
          ))}

          <div style={{ background: t.surface, borderRadius: 12, padding: 14, border: `1px solid ${t.border}`, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16 }}>
              <span style={{ color: t.textMuted }}>Total ({cart.count} item)</span>
              <span style={{ fontWeight: 800, color: t.text }}>Rp {cart.total_idr?.toLocaleString('id-ID')}</span>
            </div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, display: 'block', marginBottom: 6 }}>📍 Alamat Pengiriman</label>
            <textarea value={address} onChange={e => setAddress(e.target.value)} rows={2} style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: `1.5px solid ${t.border}`, background: t.inputBg, color: t.text, fontSize: 13, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
          </div>

          {msg && <div style={{ color: t.danger, fontSize: 13, marginBottom: 10 }}>{msg}</div>}

          <button onClick={doCheckout} style={{ width: '100%', background: t.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
            💳 Lanjut Pembayaran →
          </button>
        </>
      )}
    </div>
  );

  // ── Step: Payment ───────────────────────────────────────────────────────────
  if (step === 'payment' && summary) return (
    <div>
      <div style={{ fontSize: 18, fontWeight: 800, color: t.text, marginBottom: 16 }}>💳 Pembayaran</div>

      <div style={{ background: t.surface, borderRadius: 12, padding: 14, border: `1px solid ${t.border}`, marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: t.textFaint, marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Ringkasan</div>
        {summary.items.map((item, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0' }}>
            <span style={{ color: t.text }}>{item.emoji} {item.qty}x {item.name}</span>
            <span style={{ color: t.text, fontWeight: 600 }}>Rp {item.subtotal_idr?.toLocaleString('id-ID')}</span>
          </div>
        ))}
        <div style={{ borderTop: `1px solid ${t.border}`, marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 17, fontWeight: 800 }}>
          <span>Total</span>
          <span style={{ color: t.accent }}>Rp {summary.grand_total_idr?.toLocaleString('id-ID')}</span>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        {payOptions.map(opt => (
          <button key={opt.id} onClick={() => setPayMethod(opt.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 14px', marginBottom: 8, background: payMethod === opt.id ? t.accentBg : t.surface, border: `2px solid ${payMethod === opt.id ? t.accent : t.border}`, borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}>
            <span style={{ fontSize: 22 }}>{opt.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: t.textFaint }}>{opt.fee_label}</div>
            </div>
          </button>
        ))}
      </div>

      {payMethod === 'qris' && (
        <div style={{ background: t.surface, borderRadius: 14, padding: 20, border: `1px solid ${t.border}`, marginBottom: 14 }}>
          <QRISCode data={`NUTRIMAX-${motherId}-${summary.grand_total_idr}`} amount={summary.grand_total_idr} />
        </div>
      )}

      <button onClick={doPay} style={{ width: '100%', background: '#15803d', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
        ✅ Bayar Sekarang
      </button>
    </div>
  );

  // ── Step: Paid ──────────────────────────────────────────────────────────────
  if (step === 'paid' && order) return (
    <div style={{ textAlign: 'center' }}>
      <ProgressBar stage="paid" stages={['paid','invoice','delivering','tracking']} />
      <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d', marginBottom: 6 }}>Pembayaran Berhasil!</div>
      <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 20 }}>Order: <strong>{order.order_id}</strong> · Ref: {order.pay_ref}</div>

      <div style={{ background: t.surface, borderRadius: 12, padding: 14, border: `1px solid ${t.border}`, marginBottom: 16, textAlign: 'left' }}>
        <div style={{ fontSize: 12, color: t.textFaint, marginBottom: 6 }}>🔗 Transaction Hash</div>
        <div style={{ fontSize: 11, color: t.accent, fontFamily: 'monospace', wordBreak: 'break-all' }}>{order.tx_hash}</div>
      </div>

      <button onClick={doInvoice} style={{ width: '100%', background: t.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
        📄 Lihat Invoice →
      </button>
    </div>
  );

  // ── Step: Invoice ───────────────────────────────────────────────────────────
  if (step === 'invoice' && invoice) return (
    <div>
      <ProgressBar stage="invoice" stages={['paid','invoice','delivering','tracking']} />
      <div style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0', color: '#1e293b' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 20, fontWeight: 800 }}>🧾 INVOICE</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{invoice.invoice_no}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16, fontSize: 12 }}>
          <div><span style={{ color: '#64748b' }}>Tanggal:</span><br/><strong>{invoice.date}</strong></div>
          <div><span style={{ color: '#64748b' }}>Customer:</span><br/><strong>{invoice.customer}</strong></div>
          <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#64748b' }}>Alamat:</span><br/><strong>{invoice.address}</strong></div>
        </div>

        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 16 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left', padding: '6px 0' }}>Produk</th>
              <th style={{ textAlign: 'center' }}>Qty</th>
              <th style={{ textAlign: 'right' }}>Harga</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 0' }}>{item.product?.image_emoji || '📦'} {item.product?.name || item.product_id}</td>
                <td style={{ textAlign: 'center' }}>{item.qty}</td>
                <td style={{ textAlign: 'right' }}>Rp {((item.price_idr || item.product?.price_idr || 0) * item.qty).toLocaleString('id-ID')}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ borderTop: '2px solid #1e293b', paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800 }}>
          <span>TOTAL</span>
          <span>Rp {invoice.total_idr?.toLocaleString('id-ID')}</span>
        </div>

        <div style={{ marginTop: 12, textAlign: 'center' }}>
          <span style={{ background: '#15803d', color: '#fff', padding: '4px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>💰 {invoice.status}</span>
        </div>
      </div>

      <button onClick={doDeliver} style={{ width: '100%', marginTop: 16, background: t.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
        📦 Proses Pengiriman →
      </button>
    </div>
  );

  // ── Step: Delivering ────────────────────────────────────────────────────────
  if (step === 'delivering' && delivery) return (
    <div>
      <ProgressBar stage="delivering" stages={['paid','invoice','delivering','tracking']} />
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🚚</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>Pesanan Dikirim!</div>
      </div>

      <div style={{ background: t.surface, borderRadius: 14, padding: 16, border: `1px solid ${t.border}`, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12, marginBottom: 12 }}>
          <div><span style={{ color: t.textFaint }}>Kurir</span><br/><strong style={{ color: t.text }}>{delivery.courier}</strong></div>
          <div><span style={{ color: t.textFaint }}>No. Resi</span><br/><strong style={{ color: t.accent, fontFamily: 'monospace' }}>{delivery.tracking_no}</strong></div>
          <div><span style={{ color: t.textFaint }}>Estimasi</span><br/><strong style={{ color: t.text }}>{delivery.estimated_arrival}</strong></div>
          <div><span style={{ color: t.textFaint }}>Status</span><br/><strong style={{ color: '#15803d' }}>Shipped ✈️</strong></div>
        </div>

        <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 12 }}>
          {delivery.steps?.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', background: s.done ? '#15803d' : '#334155', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, flexShrink: 0 }}>
                {s.done ? '✓' : (i + 1)}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.done ? t.text : t.textFaint }}>{s.status}</div>
                <div style={{ fontSize: 11, color: t.textFaint }}>{s.location}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button onClick={doTracking} style={{ width: '100%', background: t.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
        🗺️ Lihat Peta Tracking →
      </button>
    </div>
  );

  // ── Step: Tracking Map ──────────────────────────────────────────────────────
  if (step === 'tracking' && tracking) return (
    <div>
      <ProgressBar stage="tracking" stages={['paid','invoice','delivering','tracking']} />
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🗺️</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: t.text }}>Live Tracking</div>
        <div style={{ fontSize: 12, color: t.textFaint }}>Estimasi tiba: {tracking.estimated_arrival}</div>
      </div>

      {/* Simulated map with route points */}
      <div style={{ background: '#1a1a2e', borderRadius: 14, padding: 16, border: `1px solid ${t.border}`, marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.1, background: 'repeating-linear-gradient(0deg, transparent, transparent 20px, #7c3aed 20px, #7c3aed 21px), repeating-linear-gradient(90deg, transparent, transparent 20px, #7c3aed 20px, #7c3aed 21px)' }} />
        <div style={{ position: 'relative' }}>
          {tracking.routes?.map((point, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: i < tracking.routes.length - 1 ? 0 : 0, padding: '8px 0' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 30 }}>
                <div style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: point.done ? '#15803d' : (i === tracking.routes.filter(r => r.done).length ? '#f59e0b' : '#334155'),
                  border: i === tracking.routes.filter(r => r.done).length ? '3px solid #fbbf24' : 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, color: '#fff',
                }}>
                  {point.done ? '✓' : (i === tracking.routes.filter(r => r.done).length ? '📍' : '○')}
                </div>
                {i < tracking.routes.length - 1 && (
                  <div style={{ width: 2, height: 20, background: point.done ? '#15803d' : '#334155', margin: '2px 0' }} />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: point.done ? '#e2e8f0' : '#64748b' }}>{point.label}</div>
                {point.time && <div style={{ fontSize: 10, color: '#64748b' }}>{new Date(point.time).toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</div>}
              </div>
              <div style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>
                {point.lat.toFixed(2)}°, {point.lng.toFixed(2)}°
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ background: t.surface, borderRadius: 12, padding: 12, border: `1px solid ${t.border}`, marginBottom: 16, fontSize: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: t.textFaint }}>Kurir</span>
          <span style={{ color: t.text, fontWeight: 600 }}>{tracking.courier}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: t.textFaint }}>No. Resi</span>
          <span style={{ color: t.accent, fontFamily: 'monospace' }}>{tracking.tracking_no}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: t.textFaint }}>Status</span>
          <span style={{ color: '#f59e0b', fontWeight: 700 }}>🚚 In Transit</span>
        </div>
      </div>

      <a href={tracking.map_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', width: '100%', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'center', textDecoration: 'none', marginBottom: 10 }}>
        🌐 Buka di Google Maps
      </a>

      <button onClick={reset} style={{ width: '100%', background: t.accent, color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
        🛍️ Belanja Lagi
      </button>
    </div>
  );

  // ── Processing state (between payment steps) ────────────────────────────────
  if (step === 'processing') return <LoadingSpinner message={loadingMsg} />;

  return null;
}
